/* eslint @typescript-eslint/no-explicit-any: "off" */
/* eslint @typescript-eslint/ban-ts-comment: "off" */

import { TreeNodeData, VisibleField } from "@tenforce/product-fields"
import {
    VisibleFieldResult,
    IItem,
    IItemsCollectionResult,
    Sorting,
    SelectableFieldOptions,
    ConceptFieldsResult,
    ConceptFieldTree,
    GroupData,
} from "../components/collection"
import { RequestErrorDataApi } from "../errors"
import { formatString } from "../util/Strings"
import { Exceptions } from "../util/Translations"
import { dataAPI } from "./api"
import {
    ODataGetArrayResponse,
    ODataGetResourceResponse,
} from "./models/api/data"
import {
    VisibleFieldResultDTO,
    SortingDTO,
    ConceptFieldGroupDTO,
    CollectionItemViewDTO,
} from "./models/dto/collection"
import { parseFieldData } from "./utils/fieldparsing"
import { IUserFormattingSettings } from "../components/applicationheader/components/usermenu"
import { IField } from "../components/fields"
import { FieldDefinitionDTO } from "./models/selectors/models"
import { parseFieldType } from "./thumbtable"

const {
    AnErrorOccurredWhileFetchingVisibleFieldsFor = "An error occurred while fetching the visible fields",
    AnErrorOccurredWhileFetchingAllConceptFields = "An error occurred while fetching the all concept fields",
    AnErrorOccurredWhileFetchingConceptFieldGroups = "An error occurred while fetching the concept groups",
} = Exceptions

const parseVisibleFieldData = (dtoData: FieldDefinitionDTO): VisibleField => {
    return {
        id: dtoData.Id,
        label: dtoData.Label,
        type: parseFieldType(dtoData),
    }
}

function capitalizeFirstLetter(
    str: "ascending" | "descending",
): "Ascending" | "Descending" {
    return (str.charAt(0).toUpperCase() + str.slice(1)) as
        | "Ascending"
        | "Descending"
}

export const getVisibleFieldsList = async (
    itemId: number,
    fieldId: string,
    odataType: string,
): Promise<VisibleFieldResult> => {
    return dataAPI
        .getJSON<ODataGetResourceResponse<VisibleFieldResultDTO>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${odataType}/Query`,
        )
        .then((response) => {
            return {
                selectedSorting: response.Sorting?.map((option) => {
                    return {
                        direction: (option.Direction === "Descending"
                            ? "descending"
                            : "ascending") as "ascending" | "descending",
                        fieldDefinitionId: option.FieldDefinitionId,
                    }
                }),
                fields: response.Fields.map(parseVisibleFieldData),
            }
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileFetchingVisibleFieldsFor,
                    fieldId,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

export const updateVisibleFieldsList = async (
    itemId: number,
    fieldId: string,
    odataType: string,
    selectedFields: string[] = [],
    selectedSorting: Sorting[] = [],
): Promise<VisibleFieldResult> => {
    const selected = selectedFields.map((field) => {
        return {
            Id: field,
        }
    })
    const sorting = selectedSorting.map((option): SortingDTO => {
        return {
            Direction: capitalizeFirstLetter(option.direction),
            FieldDefinitionId: option.fieldDefinitionId,
        }
    })
    return dataAPI
        .patchJSON<ODataGetResourceResponse<VisibleFieldResultDTO>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${odataType}/Query`,
            { Fields: selected, Sorting: sorting },
        )
        .then((response) => {
            return {
                selectedSorting: response.Sorting?.map((option) => {
                    return {
                        direction: (option.Direction === "Descending"
                            ? "descending"
                            : "ascending") as "ascending" | "descending",
                        fieldDefinitionId: option.FieldDefinitionId,
                    }
                }),
                fields: response.Fields.map(parseVisibleFieldData),
            }
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileFetchingVisibleFieldsFor,
                    fieldId,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

const parseConceptFieldData = (
    fields: ConceptFieldGroupDTO[],
    groupData: GroupData[],
): TreeNodeData<ConceptFieldTree>[] => {
    const groupedConcepts = new Map<string, TreeNodeData<ConceptFieldTree>>()
    let topLevelConcepts: TreeNodeData<ConceptFieldTree>[] = []

    const groupTotalMap = new Map<string, number>()
    groupData.forEach((dataItem) => {
        if (dataItem.id) {
            groupTotalMap.set(dataItem.id, dataItem.total)
        }
    })

    fields.forEach((item) => {
        const conceptField: TreeNodeData<ConceptFieldTree> = {
            id: item.Id,
            data: { id: item.Id, label: item.Label },
            isSelected: false,
            isSelectable: true,
            hasChildren: false,
        }

        if (item.Group && item.Group.Id) {
            const groupId = item.Group.Id
            let groupConcept = groupedConcepts.get(groupId)

            if (!groupConcept) {
                // Create new group if it doesn't exist
                groupConcept = {
                    id: groupId,
                    data: { id: groupId, label: item.Group.Label },
                    isSelected: false,
                    isSelectable: false,
                    isExpanded: true,
                    hasChildren: true,
                    children: [],
                    childrenCount: groupTotalMap.get(groupId),
                }
                groupedConcepts.set(groupId, groupConcept)
                topLevelConcepts.push(groupConcept) // Add group as a top-level item
            }

            groupConcept.children?.push(conceptField) // Add the current item as a child of the group
        } else {
            // Item has no group, add it as a top-level concept
            conceptField.hasChildren = false
            conceptField.isSelectable = true // Make non-grouped items selectable
            topLevelConcepts.push(conceptField)
        }
    })

    // all non group elements should be at the bottom on the list
    const noGroupConcepts: TreeNodeData<ConceptFieldTree>[] = []
    topLevelConcepts = topLevelConcepts.filter((concept) => {
        if (
            !concept.hasChildren ||
            (concept.children && concept.children.length === 0)
        ) {
            noGroupConcepts.push(concept)
            return false
        }
        return true
    })

    topLevelConcepts = topLevelConcepts.concat(noGroupConcepts)

    return topLevelConcepts
}

export const getAllConceptFields = async (
    filter = "",
    skip = 0,
    top = 20,
    groupData: GroupData[],
): Promise<ConceptFieldsResult> => {
    const parameters = {
        $filter: `contains(Label, '${filter}')`,
        $orderby: "Group/IsFactoryDefault desc, Group/Label asc, Label asc",
        expand: "group",
        $count: true,
        $top: top,
        $skip: skip,
    }

    return dataAPI
        .getJSON<ODataGetArrayResponse<ConceptFieldGroupDTO>>(
            "FieldDefinitions",
            parameters,
        )
        .then((response) => {
            return {
                recordsCount: response.value.length,
                fields: parseConceptFieldData(response.value, groupData),
                count: response["@count"] ?? 0,
            }
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileFetchingAllConceptFields,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

export const getConceptGroups = async (): Promise<GroupData[]> => {
    return dataAPI
        .getJSON<ODataGetArrayResponse<ConceptFieldGroupDTO>>(
            "FieldDefinitions?$apply=groupby((Group/Label,Group/id),aggregate($count%20as%20Total))",
        )
        .then((response) => {
            return response.value.map((group) => {
                return { total: group?.Total ?? 0, id: group?.Group?.Id ?? "" }
            })
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileFetchingConceptFieldGroups,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

export const getCollectionItems = async (
    itemId: number,
    fieldId: string,
    odataType: string,
    userFormattingSettings: IUserFormattingSettings,
    offset = 0,
    limit = 20,
): Promise<IItemsCollectionResult> => {
    return dataAPI
        .getJSON<ODataGetArrayResponse<CollectionItemViewDTO>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${odataType}/Query/View()?$top=${limit}&$skip=${offset}&$count=true`,
        )
        .then((response) => {
            const mappedItems = response.value.map((itemView) => {
                const mappedFields = itemView.Components.map((component) => {
                    return {
                        ...parseFieldData(component, userFormattingSettings),
                        description: "",
                        title: "",
                    } as IField
                })
                return {
                    id: itemView.ItemId,
                    fields: mappedFields,
                } as IItem
            })

            return {
                items: mappedItems,
                count: response["@count"],
            } as IItemsCollectionResult
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileFetchingVisibleFieldsFor,
                    fieldId,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

// TODO: Implement
export const getAvailableSortingFields = async (
    _itemId: number,
    fieldId: string,
    _odataType: string,
): Promise<SelectableFieldOptions> => {
    try {
        const groups = await dataAPI.getJSON<ODataGetResourceResponse<any>>(
            "FieldDefinitions?$apply=groupby((Group/Label,Group/id),aggregate($count%20as%20Total))&$count=true",
        )
        const values = await dataAPI.getJSON<ODataGetResourceResponse<any>>(
            "FieldDefinitions?expand=group",
        )

        const resultGroup = {
            id: "-1",
            totalChildrenCount: groups.value.length,
            selectedChildrenCount: 0,
            name: "",
            children: [],
        }
        // TODO: Remove these once we migrate the sorting to use the new tree.
        // @ts-ignore
        resultGroup.children = groups.value.map((group) => {
            // @ts-ignore
            const match = values.value.findAll((value) => {
                if (value.Group === null && group?.Group?.Id === "") {
                    return true
                }
                return value?.Group?.Id === group?.Group?.Id
            })
            // @ts-ignore
            const mappedValue = match.map((value) => {
                return { id: value.Id, name: value.Label }
            })

            if (group?.Group?.Id === "") {
                return {
                    id: "-1",
                    name: "Others",
                    children: mappedValue,
                    selectedChildrenCount: 0,
                    totalChildrenCount: mappedValue.length,
                }
            }
            return {
                id: group?.Group?.Id,
                name: group?.Group?.Label,
                children: mappedValue,
                selectedChildrenCount: 0,
                totalChildrenCount: mappedValue.length,
            }
        })
        return resultGroup
    } catch (error) {
        if (error instanceof Response) {
            const errorMessage = formatString(
                // TODO: Get proper error.
                AnErrorOccurredWhileFetchingVisibleFieldsFor,
                fieldId,
            )
            throw await RequestErrorDataApi.create(
                errorMessage,
                error.status,
                error,
            )
        }
        throw error
    }
}
